import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Compass, ListChecks, Volume2 } from 'lucide-react';
const finderFilters = [{
  label: 'Level',
  value: 'Beginner · Intermediate · Advanced'
}, {
  label: 'Focus',
  value: 'Attention · Body · Grounding · Compassion · Open awareness'
}, {
  label: 'Duration',
  value: '5 · 10 · 15 · 20 · 30 · 45 · 60+ minutes'
}, {
  label: 'Theme',
  value: 'Work stress · Sleep · Anxiety · Focus · Emotional balance'
}, {
  label: 'Context',
  value: 'Group · Private · Online · Corporate · Retreat'
}, {
  label: 'Group need',
  value: 'Restless group · Tired group · Mixed levels'
}];
const sessionAnatomy = ['Session objective, level and recommended context', 'Setup instructions and opening language', 'Consent-based, non-coercive invitations', 'Grounding sequence and attention anchor', 'Minute-by-minute flow with teacher cues', 'Silence windows — when to speak, when to be quiet', 'Transitions, progressions and regressions', 'Adaptations for different needs and abilities', 'Troubleshooting when the practice is not landing', 'Re-entry guidance, closing language and reflection prompts'];
const supportPillars = [{
  icon: Volume2,
  title: 'Teacher cueing & language',
  text: 'Opening, grounding, attention and body cues, transitions, invitations, re-entry and closing prompts — already written for you.'
}, {
  icon: ListChecks,
  title: 'Adaptations & flexibility',
  text: 'Easier, shorter, longer, chair-based, eyes-open, breath-free, mixed-level, online, private and trauma-informed options.'
}, {
  icon: Compass,
  title: 'Safety & professional boundaries',
  text: 'Posture choice, comfort-based alternatives, non-coercive language, consent-oriented invitations, and when to simplify or redirect.'
}];
export function TeachingLibrary() {
  return <section className="py-24 px-4 bg-cream border-y border-silver-light" data-id="element-249" data-mp="2lp2hsxy0bm0v">
      <div className="max-w-6xl mx-auto" data-id="element-250" data-mp="2qooyjhd1nkef">
        <div className="text-center mb-16" data-id="element-251" data-mp="2ipbwf6mtx85q">
          <p className="text-olive font-semibold tracking-widest uppercase text-xs mb-3" data-id="element-252" data-mp="2io94cvdrszo8">
            The core of the library
          </p>
          <h2 className="text-4xl md:text-5xl font-display font-extrabold mb-4 text-ink" data-id="element-253" data-mp="2em5qk03mpizs">
            <span className="text-brand" data-id="element-254" data-mp="2shhpkjothvmi">150 complete sessions</span> ready to teach
          </h2>
          <p className="text-xl text-graphite" data-id="element-255" data-mp="2nidbpfqy79pu">
            Organized by objective and practice · All levels covered · Nothing to build from scratch
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 mb-12" data-id="element-256" data-mp="1vqp5layrjjfg">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="lg:col-span-3 bg-paper border border-silver-light rounded-2xl p-8" data-id="element-257" data-mp="29pf48v18dr5x">
            <div className="flex items-center gap-3 mb-2" data-id="element-258" data-mp="3g468n8pkbu4p">
              <Compass className="text-brand" size={22} aria-hidden="true" data-id="element-259" data-mp="11xgfmqu3b7y4" />
              <h3 className="text-2xl font-display font-extrabold text-ink" data-id="element-260" data-mp="11zazs2xjew15">Session Finder</h3>
            </div>
            <p className="text-graphite mb-6" data-id="element-261" data-mp="swiag57x0udy">
              Find the right session in minutes instead of planning it from scratch.
            </p>
            <dl className="space-y-3" data-id="element-262" data-mp="xweeu5kiynu1">
              {finderFilters.map(filter => <div key={filter.label} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 border-b border-silver-light pb-3 last:border-0 last:pb-0" data-id="element-263" data-mp="1lm8qijt3y6cp">
                  <dt className="text-sm font-bold text-olive uppercase tracking-wider sm:w-32 shrink-0" data-id="element-264" data-mp="1buuu4kwgdmns">
                    {filter.label}
                  </dt>
                  <dd className="text-sm text-graphite" data-id="element-265" data-mp="24js1vu31g12a">{filter.value}</dd>
                </div>)}
            </dl>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: 0.05,
          ease: [0.23, 1, 0.32, 1]
        }} className="lg:col-span-2 bg-brand-soft border border-brand/30 rounded-2xl p-8" data-id="element-266" data-mp="29cuxe7rdlkl4">
            <div className="flex items-center gap-3 mb-4" data-id="element-267" data-mp="6pplwgppkywl">
              <Clock className="text-brand-dark" size={22} aria-hidden="true" data-id="element-268" data-mp="6r93can3pfzy" />
              <h3 className="text-xl font-display font-extrabold text-ink" data-id="element-269" data-mp="13ex8zt5z7g2m">Session durations</h3>
            </div>
            <p className="text-sm text-graphite mb-5" data-id="element-270" data-mp="zen0o9upodkl">
              Useful whether you have a brief intervention or a complete class.
            </p>
            <ul className="grid grid-cols-2 gap-2 text-sm" data-id="element-271" data-mp="potslits2gl3">
              {['5 min', '10 min', '15 min', '20 min', '30 min', '45 min', '60+ min', 'Retreat'].map(duration => <li key={duration} className="bg-paper border border-brand/20 rounded-lg px-3 py-2 text-center font-medium text-ink" data-id="element-272" data-mp="1icfv0nr9rf6d">
                    {duration}
                  </li>)}
            </ul>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="bg-paper border border-silver-light rounded-2xl p-8 mb-8" data-id="element-273" data-mp="1zkgt8g9jhi24">
          <h3 className="text-xl font-display font-extrabold text-ink mb-6" data-id="element-274" data-mp="2dk8e90yh3ypm">
            Every session includes
          </h3>
          <ul className="grid md:grid-cols-2 gap-x-8 gap-y-3" data-id="element-275" data-mp="2ievs85vupapy">
            {sessionAnatomy.map(item => <li key={item} className="flex items-start gap-3" data-id="element-276" data-mp="2rd7sz0qflow4">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gold shrink-0" aria-hidden="true" data-id="element-277" data-mp="2rf0zmg11o944" />
                <span className="text-sm text-graphite" data-id="element-278" data-mp="w9jitbeu6413">{item}</span>
              </li>)}
          </ul>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6" data-id="element-279" data-mp="sa0x4df6xox7">
          {supportPillars.map((pillar, index) => <motion.div key={pillar.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: index * 0.05,
          ease: [0.23, 1, 0.32, 1]
        }} className="bg-cream-light border border-silver-light rounded-2xl p-6 flex flex-col" data-id="element-280" data-mp="1615pcug8bexi" data-mpi={index}>
              <pillar.icon className="text-brand mb-4" size={22} aria-hidden="true" data-id="element-281" data-mp="1oy6zeton458b" />
              <h4 className="font-display font-extrabold text-lg text-ink mb-2" data-id="element-282" data-mp="1k0x8tapilniw">{pillar.title}</h4>
              <p className="text-sm text-graphite leading-relaxed" data-id="element-283" data-mp="3qqv4z9orb4r9">{pillar.text}</p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}